import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ErrorCode, type UnresolvedDepartment } from '@bliks-io/contracts';
import { initializeKeys } from '@/api/query-keys';
import { useCreateDepartmentsBulk } from '@/api/mutations/useDepartments';
import { useInitializeAssignUserPlacement } from '@/api/mutations/useInitializeAssignUserPlacement';
import { useInitializeUsers } from '@/api/mutations/useInitializeUsers';
import { useInitializeSystems } from '@/api/mutations/useInitializeSystems';
import { useInitializeProcesses } from '@/api/mutations/useInitializeProcesses';
import { useCompleteInitialSetup } from '@/api/mutations/useCompleteInitialSetup';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useBulkSendOrgMemberInvitations } from '@/api/mutations/useOrgMembers';
import type { ApiError } from '@/lib/agent.types';
import type { ParsedSystemRow } from '@/lib/excel';
import type { InitializeProcessDraft } from '@/components/bulk-upload';
import type { WizardFlushBaselines } from './utils/flushBaselines';
import { mapApiErrorsToRows, translateWizardSubmitError } from './utils/handleSaveErrors';
import { commitPlacementRows } from './utils/protectedRows';
import {
  applyResolvedManagerEmails,
  type ResolvedManagerEmailUpdate
} from './utils/resolvedManagerEmails';
import type { ParsedDepartment, ParsedUser } from './utils/excelParsers';
import type { OrgFunctionSurfaceDrafts } from './steps/OrgFunctionSurfaceStep';
import { useOrgFunctionSurfaceSubmit } from './useOrgFunctionSurfaceSubmit';
import { useResolutionSubmit } from './useResolutionSubmit';
import type { WizardValidationContext } from './useWizardValidationContext';
import {
  departmentsStepSpec,
  processesStepSpec,
  systemsStepSpec,
  usersStepSpec,
  type WizardSeeds
} from './anchorStepSpecs';
import { submitAnchorStep } from './anchorStepSubmit';
import { reportStepError, sendNeverInvitedInvites } from './stepSubmit';
import { nextStepId, type WizardProgress, type WizardStepId } from './wizardSteps';

export interface WizardDrafts {
  departments: ParsedDepartment[];
  users: ParsedUser[];
  managerAssignments: Map<string, string>;
  functionDrafts: OrgFunctionSurfaceDrafts;
  teamDrafts: OrgFunctionSurfaceDrafts;
  systems: ParsedSystemRow[];
  processes: InitializeProcessDraft[];
}

interface UseWizardSubmitArgs {
  currentStep: WizardStepId;
  drafts: WizardDrafts;
  progress: WizardProgress;
  seeds: WizardSeeds;
  /** Per-step flush baselines shared with the autosave engine — Weiter diffs against them and clears them on a committed step. */
  baselines: WizardFlushBaselines;
  setBaselines: React.Dispatch<React.SetStateAction<WizardFlushBaselines>>;
  /** The page's single validation context instance, shared with the autosave engine. */
  validation: WizardValidationContext;
  /** Autosave settle — every Weiter submit drains the silent flush first so the two can never double-commit. */
  settleAutosave: () => Promise<void>;
  setDepartments: React.Dispatch<React.SetStateAction<ParsedDepartment[]>>;
  setUsers: React.Dispatch<React.SetStateAction<ParsedUser[]>>;
  setFunctionDrafts: (drafts: OrgFunctionSurfaceDrafts) => void;
  setTeamDrafts: (drafts: OrgFunctionSurfaceDrafts) => void;
  setSystems: React.Dispatch<React.SetStateAction<ParsedSystemRow[]>>;
  setProcesses: React.Dispatch<React.SetStateAction<InitializeProcessDraft[]>>;
  setManagerAssignments: React.Dispatch<React.SetStateAction<Map<string, string>>>;
  setSubmitError: (error: string | undefined) => void;
  /** Also invite every never-invited member when the wizard is completed. */
  inviteOnFinish: boolean;
  /**
   * Unsaved dirt from the autosave engine — pending removals never flush
   * silently, so Abschließen must refuse instead of silently dropping a
   * confirmed delete-all left on another step.
   */
  hasPendingChanges: boolean;
  goTo: (step: WizardStepId) => void;
  onFinished: () => void;
}

/**
 * Per-step Weiter handlers of the setup wizard. Steps 1-2 are the structural
 * anchor: once stamped, new rows append additively, edits of seeded rows
 * update in place by id, and removals of seeded rows delete surgically —
 * blocked with a banner when a required reference exists (ADR 0011, third
 * amendment).
 * The optional steps submit only when drafts exist — Weiter over an empty
 * optional step is plain navigation, identical to Überspringen.
 */
export function useWizardSubmit({
  currentStep,
  drafts,
  progress,
  seeds,
  baselines,
  setBaselines,
  validation,
  settleAutosave,
  setDepartments,
  setUsers,
  setFunctionDrafts,
  setTeamDrafts,
  setSystems,
  setProcesses,
  setManagerAssignments,
  setSubmitError,
  inviteOnFinish,
  hasPendingChanges,
  goTo,
  onFinished
}: UseWizardSubmitArgs) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createDepartmentsBulk = useCreateDepartmentsBulk();
  const initializeUsers = useInitializeUsers();
  const assignUserPlacement = useInitializeAssignUserPlacement();
  const initializeSystems = useInitializeSystems();
  const initializeProcesses = useInitializeProcesses();
  const completeInitialSetup = useCompleteInitialSetup();
  const bulkSendInvitations = useBulkSendOrgMemberInvitations();

  const { orgMembers, emailToUser } = validation;

  // Syncs a manual Manager-auflösen resolution's email into the departments
  // draft AND (when one is already frozen) its flush baseline — the draft
  // alone fixes the stale display on the departments step, and mirroring the
  // value onto the baseline keeps the row reading as unchanged so autosave
  // doesn't re-send a pointless update for a manager the backend already
  // resolved this session (the department's draft row may not even exist,
  // e.g. imported+resolved before the departments step was ever visited —
  // applyResolvedManagerEmails is then a no-op).
  const applyResolvedManagerEmailsToDrafts = useCallback(
    (updates: ResolvedManagerEmailUpdate[]) => {
      if (updates.length === 0) return;
      setDepartments((current) => applyResolvedManagerEmails(current, updates));
      setBaselines((prev) =>
        prev.departments
          ? { ...prev, departments: applyResolvedManagerEmails(prev.departments, updates) }
          : prev
      );
    },
    [setBaselines, setDepartments]
  );

  const { submitResolutions, isResolutionPending } = useResolutionSubmit({
    managerAssignments: drafts.managerAssignments,
    setManagerAssignments,
    emailToUser,
    goTo,
    onDepartmentsResolved: applyResolvedManagerEmailsToDrafts
  });

  const handleApiError = useCallback(
    (error: unknown, failedToastKey: string) => {
      const apiError = error as ApiError;
      setSubmitError(translateWizardSubmitError(apiError, t));

      const { updatedDepartments, updatedUsers } = mapApiErrorsToRows(
        apiError,
        t,
        drafts.departments,
        drafts.users
      );
      if (updatedDepartments) setDepartments(updatedDepartments);
      if (updatedUsers) setUsers(updatedUsers);

      toastApiError(error, failedToastKey);
      console.error(error);
    },
    [drafts.departments, drafts.users, setDepartments, setSubmitError, setUsers, t]
  );

  // Linear Weiter hop: advance to the next step in wizardSteps order. Used by
  // every step whose successor is fixed; the conditional hops (users -> resolve
  // vs functions) stay explicit at their call site.
  const goToNext = useCallback(() => {
    const next = nextStepId(currentStep);
    if (next) goTo(next);
  }, [currentStep, goTo]);

  // Every path that resets an anchor/surface draft back to seed-display also
  // drops its flush baseline — the next edit (or silent flush) re-establishes
  // one from the then-current seeds.
  const clearBaseline = useCallback(
    (key: keyof WizardFlushBaselines) => setBaselines((prev) => ({ ...prev, [key]: undefined })),
    [setBaselines]
  );

  const submitDepartments = useCallback(
    () =>
      submitAnchorStep({
        spec: departmentsStepSpec,
        drafts,
        baselines,
        seeds,
        progress,
        validation,
        setRows: setDepartments,
        clearBaseline: () => clearBaseline('departments'),
        mutate: createDepartmentsBulk.mutateAsync,
        // A departments edit can stage NEW unresolved manager emails long
        // after the users step is done. The page's render-derived resolve
        // detour only fires for steps PAST the sub-step, and 'users' sits
        // before it — so detour directly instead of re-visiting the done
        // Mitarbeiter step. Cache peek: the awaited invalidations refetched
        // it, while this closure's progress predates the commit.
        navigateNext: () => {
          const openUnresolved = queryClient.getQueryData<UnresolvedDepartment[]>(
            initializeKeys.unresolvedDepartments()
          );
          if (progress.usersDone && openUnresolved && openUnresolved.length > 0) goTo('resolve');
          else goToNext();
        },
        validationRequiredKey: 'bulkOnboarding.departments.toast.validationRequired',
        fixErrorsKey: 'bulkOnboarding.departments.toast.fixErrors',
        createSuccessKey: 'bulkOnboarding.departments.toast.createSuccess',
        createFailedKey: 'bulkOnboarding.departments.toast.createFailed',
        t,
        onApiError: handleApiError
      }),
    [
      baselines,
      clearBaseline,
      createDepartmentsBulk,
      drafts,
      goTo,
      goToNext,
      handleApiError,
      progress,
      queryClient,
      seeds,
      setDepartments,
      t,
      validation
    ]
  );

  const submitUsers = useCallback(() => {
    // Without the member lists the protected-row partition and the
    // unrepresented-member check silently no-op — protected members would be
    // sent through the add-users pipeline the backend rejects wholesale.
    // Refuse until they have loaded.
    if (!usersStepSpec.canSubmit(validation)) {
      toast.error(t('initialSetup.usersImport.membersNotLoaded'));
      return Promise.resolve();
    }
    return submitAnchorStep({
      spec: usersStepSpec,
      drafts,
      baselines,
      seeds,
      progress,
      validation,
      setRows: setUsers,
      clearBaseline: () => clearBaseline('users'),
      mutate: initializeUsers.mutateAsync,
      protectedCommit: {
        commitRows: (rows: Array<ParsedUser & { entityId: string }>) =>
          commitPlacementRows(rows, assignUserPlacement.mutateAsync),
        failedToastKey: 'initialSetup.protectedRows.placementFailed'
      },
      // Manager resolution runs synchronously inside the users import
      // request; the awaited invalidations already refetched progress, so
      // the page's render-derived guard detours into the resolve sub-step
      // when unresolved managers remain.
      navigateNext: () => goTo('functions'),
      validationRequiredKey: 'bulkOnboarding.users.toast.validationRequired',
      fixErrorsKey: 'bulkOnboarding.users.toast.fixErrors',
      createSuccessKey: 'bulkOnboarding.users.toast.createSuccess',
      createFailedKey: 'bulkOnboarding.users.toast.createFailed',
      t,
      onApiError: handleApiError
    });
  }, [
    assignUserPlacement,
    baselines,
    clearBaseline,
    drafts,
    goTo,
    initializeUsers,
    progress,
    seeds,
    setUsers,
    handleApiError,
    t,
    validation
  ]);

  const { submitOrgFunctionSurface, isOrgFunctionSurfacePending } = useOrgFunctionSurfaceSubmit({
    validation,
    setSubmitError
  });

  const submitFunctions = useCallback(
    () =>
      submitOrgFunctionSurface('role', drafts.functionDrafts, setFunctionDrafts, () => {
        clearBaseline('functions');
        goToNext();
      }),
    [clearBaseline, drafts.functionDrafts, goToNext, setFunctionDrafts, submitOrgFunctionSurface]
  );

  const submitTeams = useCallback(
    () =>
      submitOrgFunctionSurface('team', drafts.teamDrafts, setTeamDrafts, () => {
        clearBaseline('teams');
        goToNext();
      }),
    [clearBaseline, drafts.teamDrafts, goToNext, setTeamDrafts, submitOrgFunctionSurface]
  );

  const submitSystems = useCallback(() => {
    return submitAnchorStep({
      spec: systemsStepSpec,
      drafts,
      baselines,
      seeds,
      progress,
      validation,
      setRows: setSystems,
      clearBaseline: () => clearBaseline('systems'),
      mutate: initializeSystems.mutateAsync,
      navigateNext: goToNext,
      fixErrorsKey: 'initialSetup.systems.toast.fixErrors',
      createSuccessKey: 'initialSetup.systems.toast.createSuccess',
      createFailedKey: 'initialSetup.systems.toast.createFailed',
      t,
      onApiError: handleApiError
    });
  }, [
    baselines,
    clearBaseline,
    drafts,
    goToNext,
    handleApiError,
    initializeSystems,
    progress,
    seeds,
    setSystems,
    t,
    validation
  ]);

  const submitProcesses = useCallback(() => {
    return submitAnchorStep({
      spec: processesStepSpec,
      drafts,
      baselines,
      seeds,
      progress,
      validation,
      setRows: setProcesses,
      clearBaseline: () => clearBaseline('processes'),
      mutate: initializeProcesses.mutateAsync,
      navigateNext: goToNext,
      fixErrorsKey: 'bulkOnboarding.processes.toast.fixErrors',
      createSuccessKey: 'bulkOnboarding.processes.toast.createSuccess',
      createFailedKey: 'bulkOnboarding.processes.toast.createFailed',
      t,
      onApiError: handleApiError
    });
  }, [
    baselines,
    clearBaseline,
    drafts,
    goToNext,
    handleApiError,
    initializeProcesses,
    progress,
    seeds,
    setProcesses,
    t,
    validation
  ]);

  const finish = useCallback(async () => {
    // The settle before this call flushed creates/updates, but removals
    // (a cleared table) commit only via their step's Weiter — completing
    // now would silently drop them.
    if (hasPendingChanges) {
      toast.error(t('initialSetup.overview.toast.pendingChanges'));
      return;
    }
    try {
      await completeInitialSetup.mutateAsync();
      // Opt-in, best-effort, and only after completion has cleared the setup
      // gate: invite every member the wizard added but never invited.
      if (inviteOnFinish) {
        await sendNeverInvitedInvites(orgMembers ?? [], bulkSendInvitations.mutateAsync, t);
      }
      toast.success(t('initialSetup.overview.toast.completed'));
      onFinished();
    } catch (error) {
      const apiError = error as ApiError;
      // Integrity gate: gaps created by surgical removals block completion —
      // the overview renders them as an alert with jump links driven by the
      // summary's gap counts (ADR 0011, third amendment). Refetching the
      // summary is what makes the alert appear immediately after THIS
      // failure; awaited so the toast never outruns the fresh counts.
      if (apiError.message === ErrorCode.INITIAL_SETUP_INCOMPLETE) {
        await queryClient.invalidateQueries({ queryKey: initializeKeys.summary() });
        toast.error(t('initialSetup.overview.toast.completeFailed'));
        console.error(error);
        return;
      }
      reportStepError(error, 'initialSetup.overview.toast.completeFailed', t, setSubmitError);
    }
  }, [
    bulkSendInvitations,
    completeInitialSetup,
    hasPendingChanges,
    inviteOnFinish,
    onFinished,
    orgMembers,
    queryClient,
    setSubmitError,
    t
  ]);

  const submitCurrentStep = useCallback(async () => {
    // Drain the silent autosave (cancel its debounce, await an in-flight
    // flush) BEFORE classifying — the ordering travels with the submit so no
    // caller can double-commit rows a concurrent flush is sending.
    await settleAutosave();
    setSubmitError(undefined);
    switch (currentStep) {
      // Purely informational screens — Weiter is plain navigation.
      case 'welcome':
      case 'outcomes':
        return goToNext();
      case 'departments':
        return submitDepartments();
      case 'users':
        return submitUsers();
      case 'resolve':
        return submitResolutions();
      case 'functions':
        return submitFunctions();
      case 'teams':
        return submitTeams();
      case 'systems':
        return submitSystems();
      case 'processes':
        return submitProcesses();
      case 'overview':
        return finish();
      default:
        return currentStep satisfies never;
    }
  }, [
    currentStep,
    finish,
    goToNext,
    setSubmitError,
    settleAutosave,
    submitDepartments,
    submitFunctions,
    submitProcesses,
    submitResolutions,
    submitSystems,
    submitTeams,
    submitUsers
  ]);

  const isSaving =
    isResolutionPending ||
    createDepartmentsBulk.isPending ||
    initializeUsers.isPending ||
    assignUserPlacement.isPending ||
    isOrgFunctionSurfacePending ||
    initializeSystems.isPending ||
    initializeProcesses.isPending ||
    completeInitialSetup.isPending ||
    bulkSendInvitations.isPending;

  return { submitCurrentStep, isSaving };
}
